<script setup lang="ts">
import { onBeforeUnmount, onErrorCaptured, onMounted, provide, ref, shallowRef, watch, type Component } from 'vue'
import type { ViewData } from '@/lib/api'
import { getViewComponent, getViewModule, holdStyle, putViewResize, viewKey, type ViewState } from '@/lib/view'

const props = defineProps<{
  component: string
  styleText: string
  data: ViewData | null
  stateKey?: string
}>()

const emit = defineEmits<{ mark: [time: string]; diagnostics: [messages: string[]] }>()

const el = ref<HTMLElement | null>(null)
const failure = ref<string | null>(null)
const view = shallowRef<Component | null>(null)
const viewId = ref('')

const state: ViewState = {
  rows: ref([]),
  annotations: ref([]),
  from: ref(null),
  to: ref(null),
  error: ref(null),
  mark: (time) => emit('mark', time),
  stateKey: props.stateKey,
}

provide(viewKey, state)

onErrorCaptured((err) => {
  state.error.value = err instanceof Error ? err.message : String(err)
  return false
})

const putData = (data: ViewData | null) => {
  if (!data) return
  state.rows.value = data.rows ?? []
  state.annotations.value = data.annotations ?? []
  state.from.value = data.from
  state.to.value = data.to
  state.error.value = data.error ?? null
}

let releaseStyle: (() => void) | null = null
let token = 0

const unmount = () => {
  view.value = null
  releaseStyle?.()
  releaseStyle = null
}

const render = async () => {
  const mine = ++token
  if (!props.component.trim()) {
    unmount()
    return
  }
  try {
    const built = await getViewModule(props.component, props.styleText)
    if (mine !== token || !el.value) return
    if (built.diagnostics.length || !built.module) {
      const messages = built.diagnostics.map((d) => `${d.line ? `${d.line}:${d.column} ` : ''}${d.message}`)
      failure.value = messages.join('\n') || 'The component did not build'
      emit('diagnostics', messages)
      return
    }
    failure.value = null
    emit('diagnostics', [])
    const component = getViewComponent(built)
    const release = holdStyle(built.id, built.style)
    unmount()
    releaseStyle = release
    viewId.value = built.id
    view.value = component
  } catch (err) {
    failure.value = err instanceof Error ? err.message : 'The component did not build'
  }
}

let release: (() => void) | null = null

onMounted(() => {
  putData(props.data)
  void render()
  if (el.value) release = putViewResize(el.value)
})

watch(() => [props.component, props.styleText], () => void render())
watch(() => props.data, putData)

onBeforeUnmount(() => {
  token++
  release?.()
  unmount()
})
</script>

<template>
  <div class="view-mount">
    <div ref="el" class="view-root"><component :is="view" v-if="view" :key="viewId" /></div>
    <pre v-if="failure" class="view-failure">{{ failure }}</pre>
  </div>
</template>

<style scoped>
.view-mount {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  container-type: size;
}

.view-root {
  width: 100%;
  height: 100%;
}

.view-root > :deep(*) {
  box-sizing: border-box;
  padding: var(--view-padding, var(--view-inset-top) var(--view-inset) var(--view-inset));
}

:global(.view-resizing .view-root) {
  contain: layout paint;
}

:global(.view-resizing .view-root [_echarts_instance_] > div),
:global(.view-resizing .view-root [_echarts_instance_] canvas),
:global(.view-resizing .view-root [_echarts_instance_] svg) {
  width: 100% !important;
  height: 100% !important;
}

.view-failure {
  position: absolute;
  inset: 0;
  margin: 0;
  overflow: auto;
  color: var(--mar-danger-color);
  font-family: var(--mar-font-mono, monospace);
  font-size: 0.75em;
  white-space: pre-wrap;
}
</style>
